import { useEffect, useRef, useState } from "react";
import { api, coincide, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Camera, Check } from "lucide-react";

const hoy = () => new Date().toISOString().slice(0, 10);

export const PagoManualDialog = ({ open, onOpenChange, alumnoId, sedeId, inicial, titulo, onHecho }) => {
  const [planes, setPlanes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    plan_id: "", metodo: "efectivo", fecha_pago: hoy(), fecha_inicio: hoy(),
    importe: "", incluir_matricula: false, incluir_seguro: false, nota: "", recibo_file_id: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      plan_id: "", metodo: "efectivo", fecha_pago: hoy(), fecha_inicio: hoy(),
      importe: "", incluir_matricula: false, incluir_seguro: false, nota: "", recibo_file_id: "",
      ...(inicial || {}),
    });
    // En un cobro manual valen TODOS los planes de la sede, también los ocultos a las
    // familias y los exclusivos: se está registrando algo ya acordado con ellas.
    api.get("/planes").then(({ data }) =>
      setPlanes(data.filter((p) => (!sedeId || p.sede_id === sedeId))
        .sort((a, b) => (b.activo ? 1 : 0) - (a.activo ? 1 : 0)
          || a.nombre.localeCompare(b.nombre))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sedeId]);

  const plan = planes.find((p) => p.id === form.plan_id);
  const visibles = planes.filter((p) => coincide(
    [p.nombre, p.categoria, p.tipo, p.padre_nombre, String(p.importe)].join(" "), busqueda));
  const total = form.importe !== "" ? Number(form.importe)
    : (plan ? Number(plan.importe || 0)
      + (form.incluir_matricula && plan.matricula ? Number(plan.matricula_importe || 0) : 0)
      + (form.incluir_seguro && plan.seguro ? Number(plan.seguro_importe || 0) : 0) : 0);

  const subirRecibo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, recibo_file_id: data.file_id }));
      toast.success("Recibo adjuntado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const guardar = async () => {
    if (!form.plan_id) return toast.error("Elige el plan o producto pagado");
    if (!form.recibo_file_id) return toast.error("Adjunta la foto del recibo");
    setGuardando(true);
    try {
      const { data } = await api.post(`/alumnos/${alumnoId}/pagos-manuales`, {
        ...form, importe: form.importe === "" ? null : Number(form.importe),
      });
      toast.success(data.factura ? `Pago registrado · factura ${data.factura}` : "Pago registrado");
      onOpenChange(false);
      onHecho?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="pago-manual-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{titulo || "Registrar pago manual"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Plan o producto pagado</Label>
            <Input value={busqueda} placeholder="Buscar plan por nombre o precio…"
              onChange={(e) => setBusqueda(e.target.value)} data-testid="pago-manual-buscar-input" />
            <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v, importe: "" })}>
              <SelectTrigger data-testid="pago-manual-plan-select"><SelectValue placeholder="Elige un plan" /></SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                {visibles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nombre} · {p.importe} €
                    {!p.activo ? " · oculto a familias" : ""}
                    {p.padre_nombre ? ` · exclusivo de ${p.padre_nombre}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground" data-testid="pago-manual-plan-contador">
              {visibles.length} de {planes.length} plan(es) de esta sede
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Método</Label>
              <Select value={form.metodo} onValueChange={(v) => setForm({ ...form, metodo: v })}>
                <SelectTrigger data-testid="pago-manual-metodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia bancaria</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Importe cobrado (€)</Label>
              <Input type="number" step="0.01" value={form.importe} placeholder={total ? total.toFixed(2) : "0.00"}
                onChange={(e) => setForm({ ...form, importe: e.target.value })}
                data-testid="pago-manual-importe-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Fecha del pago</Label>
              <Input type="date" value={form.fecha_pago}
                onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })}
                data-testid="pago-manual-fecha-input" />
            </div>
            <div className="grid gap-2">
              <Label>Empieza a contar el</Label>
              <Input type="date" value={form.fecha_inicio}
                onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                data-testid="pago-manual-inicio-input" />
            </div>
          </div>
          {plan?.matricula && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.incluir_matricula} data-testid="pago-manual-matricula-check"
                onCheckedChange={(v) => setForm({ ...form, incluir_matricula: !!v, importe: "" })} />
              Incluye matrícula anual ({plan.matricula_importe} €)
            </label>
          )}
          {plan?.seguro && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.incluir_seguro} data-testid="pago-manual-seguro-check"
                onCheckedChange={(v) => setForm({ ...form, incluir_seguro: !!v, importe: "" })} />
              Incluye seguro de recuperación de clases ({plan.seguro_importe} €)
            </label>
          )}
          <div className="grid gap-2">
            <Label>Foto del recibo</Label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden
              onChange={subirRecibo} data-testid="pago-manual-recibo-input" />
            <Button type="button" variant={form.recibo_file_id ? "outline" : "secondary"}
              className="rounded-lg justify-start" disabled={subiendo}
              onClick={() => fileRef.current?.click()} data-testid="pago-manual-recibo-btn">
              {subiendo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : form.recibo_file_id ? <Check className="h-4 w-4 mr-2 text-emerald-600" />
                : <Camera className="h-4 w-4 mr-2" />}
              {form.recibo_file_id ? "Recibo adjuntado · cambiar" : "Hacer foto o subir el recibo"}
            </Button>
          </div>
          <div className="grid gap-2">
            <Label>Nota interna</Label>
            <Textarea value={form.nota} placeholder="Nº de operación, quién lo cobró…"
              onChange={(e) => setForm({ ...form, nota: e.target.value })}
              data-testid="pago-manual-nota-input" />
          </div>
          <p className="text-sm text-muted-foreground" data-testid="pago-manual-total">
            Total a facturar: <b className="text-foreground">{total.toFixed(2)} €</b>. Se emitirá factura
            numerada y la familia la recibirá por email.
          </p>
        </div>
        <DialogFooter>
          <Button className="rounded-full font-semibold" onClick={guardar} disabled={guardando}
            data-testid="guardar-pago-manual-btn">
            {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Registrar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
